// src/libs/transform/article-transform.ts

import type {
  Article,
  ArticleAR,
  ArticleAuthor,
  ArticleBizType,
  ArticleChipGiveMeta,
  ArticleEmoji,
  ArticleMessage,
  ArticleStats,
  ArticleTable,
  ArticleTypeRaw,
} from '@/model/article';
import type {
  ArticleListItem,
  ArticleMessageItem,
  MessageGetCoinDataRaw,
  ChipGiveDataRaw,
} from '@/libs/services/article';

/**
 * shortData 單一項目本地型別
 */
type ShortDataItemRaw = {
  cover?: string;
  crtime?: string;
  key?: string;
  ledtime?: string;
  short_id?: string;
  user_id?: string;
  video_url?: string;
};

/* -------------------------------------------------------
 * 共用小工具
 * ----------------------------------------------------- */

function toBoolFlag(value: unknown | undefined): boolean | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
  }
  return !!value;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  }
  return [];
}

function normalizeTable(table: unknown): ArticleTable {
  const t = String(table || '');
  if (
    t === 'privilege_kol' ||
    t === 'collection' ||
    t === 'store_article' ||
    t === 'kol_privilege_article'
  ) {
    return t;
  }
  return 'collection';
}

function mapBizType(table: ArticleTable, articleType: ArticleTypeRaw | undefined): ArticleBizType {
  const t = articleType as ArticleTypeRaw | undefined;

  switch (table) {
    case 'privilege_kol':
      return t === 'pair' ? 'pair' : 'privilege';

    case 'collection':
      if (t === 'message_get_coin') return 'message_get_coin';
      if (t === 'chip_give') return 'coin_give';
      if (t === 'chip_receive') return 'coin_receive';
      if (t === 'chip_exchange') return 'coin_exchange';
      return 'mood';

    case 'store_article':
      return 'store_post';

    case 'kol_privilege_article':
      if (t === 'secret') return 'secret';
      if (t === 'url') return 'exclusive_url';
      if (t === 'lottery') return 'lottery';
      if (t === 'discuss') return 'topic';
      return 'kol_privilege';

    default:
      return 'mood';
  }
}

function normalizeDate(dateStr?: string): string {
  if (!dateStr) return '';
  const s = String(dateStr);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (s.includes('T')) return s.slice(0, 10);
  return s;
}

function normalizeDateTime(dateTimeStr?: string): string {
  if (!dateTimeStr) return '';
  const s = String(dateTimeStr).trim();
  if (!s) return '';

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    if (s.length >= 16) return s.slice(0, 16);
    if (s.length >= 10) return s.slice(0, 10);
    return s;
  }

  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (isoMatch) {
    const [, d, hm] = isoMatch;
    return `${d} ${hm}`;
  }

  return s;
}

function parsePhotoList(raw: ArticleListItem): string[] {
  const fromPhotoData = toStringArray(raw.photo_data);
  if (fromPhotoData.length > 0) return fromPhotoData;

  if (typeof raw.photo === 'string' && raw.photo.trim() !== '') {
    try {
      const parsed = JSON.parse(raw.photo);
      return toStringArray(parsed);
    } catch {
      // ignore
    }
  }

  return [];
}

function parseShortData(rawShort: unknown): ShortDataItemRaw[] | undefined {
  if (!rawShort) return undefined;

  let list: unknown;

  if (Array.isArray(rawShort)) {
    list = rawShort;
  } else if (typeof rawShort === 'object') {
    list = [rawShort];
  } else if (typeof rawShort === 'string') {
    try {
      const parsed = JSON.parse(rawShort);
      if (Array.isArray(parsed)) list = parsed;
      else if (parsed && typeof parsed === 'object') list = [parsed];
      else return undefined;
    } catch {
      return undefined;
    }
  } else {
    return undefined;
  }

  if (!Array.isArray(list)) return undefined;

  const normalized = (list as unknown[]).filter((item): item is ShortDataItemRaw => {
    if (!item || typeof item !== 'object') return false;
    const obj = item as ShortDataItemRaw;
    return !!(obj.short_id || obj.video_url);
  });

  return normalized.length > 0 ? normalized : undefined;
}

/** list + detail 都可能出現的擴充欄位（避免 any） */
type ArticleRawExtended = ArticleListItem & {
  user_id?: number | string;
  womo_id?: string | null;
  user_name?: string | null;
  user_u_photo?: string | null;
  user_photo?: string | null;
  intervals_time?: string | number | null;
  youtube_url?: string | null;
  is_fav?: boolean;
  is_praise?: boolean;
  tag_data?: unknown;

  message_get_coin_data?: MessageGetCoinDataRaw | MessageGetCoinDataRaw[] | null;
  chip_give_data?: ChipGiveDataRaw | ChipGiveDataRaw[] | null;
  coin_start_date?: string;
  coin_end_date?: string;

  shortData?: unknown;

  /** ✅ 單篇 detail（你貼的後端）會出現的 camelCase 欄位 */
  arBranchName?: string | null;
  arSpaceId?: string | number | null;
  arSpaceType?: 'branch' | 'ar_space' | string | null;
  arStoreId?: string | number | null;
  arTitle?: string | null;

  /** ✅ 舊 snake_case（有些 API 可能還在用） */
  ar_type?: 'ar_space' | 'branch' | '' | null;
  ar_space_id?: string | number | '' | null;
  ar_is_active?: string | number | 0 | 1 | '' | null;
  ar_store_id?: number | string | null;
  ar_branch_name?: string | null;
  ar_title?: string | null;
};

function normalizeArSpaceType(value: unknown): 'ar_space' | 'branch' | null {
  const v = typeof value === 'string' ? value.trim() : '';
  if (v === 'ar_space') return 'ar_space';
  if (v === 'branch') return 'branch';
  return null;
}

function normalizeArIsActive(value: unknown, fallback = true): boolean {
  // 後端沒給 ar_is_active → 預設 true（不要害你 AR 貼文變 false）
  if (value === null || value === undefined || value === '') return fallback;
  return toNumber(value) === 1;
}

/* -------------------------------------------------------
 * normalizeArticle：後端 ArticleListItem/Detail -> 前端 Article
 * ----------------------------------------------------- */

export function normalizeArticle(raw: ArticleListItem): Article {
  const rawExt = raw as ArticleRawExtended;

  const table = normalizeTable(raw.table);
  const articleTypeRaw = raw.article_type as ArticleTypeRaw | undefined;
  const bizType = mapBizType(table, articleTypeRaw);

  const tableId = raw.table_id !== undefined && raw.table_id !== null ? String(raw.table_id) : '';
  const id = `${table}_${tableId}`;

  /* ---------------- 作者資訊 ---------------- */

  let authorId = '';
  if (raw.kol_id !== undefined && raw.kol_id !== null) authorId = String(raw.kol_id);
  else if (rawExt.user_id !== undefined && rawExt.user_id !== null)
    authorId = String(rawExt.user_id);
  else if (typeof raw.womo_id === 'string' && raw.womo_id.trim()) authorId = raw.womo_id.trim();

  let authorName = (raw.kol_name ?? '').trim();
  if (!authorName && typeof rawExt.user_name === 'string') authorName = rawExt.user_name.trim();
  if (!authorName && typeof raw.womo_id === 'string') authorName = raw.womo_id.trim();

  let authorAvatar = (raw.kol_u_photo ?? '').trim() || (raw.kol_photo ?? '').trim();
  if (!authorAvatar) {
    const userUPhoto = (rawExt.user_u_photo ?? '').trim();
    const userPhoto = (rawExt.user_photo ?? '').trim();
    if (userUPhoto || userPhoto) authorAvatar = userUPhoto || userPhoto;
  }

  const author: ArticleAuthor = {
    id: authorId,
    name: authorName,
    avatar: authorAvatar,
  };

  const womoId = typeof raw.womo_id === 'string' && raw.womo_id.trim() ? raw.womo_id.trim() : null;

  /* ---------------- 圖片 / emoji ---------------- */

  const cover = raw.cover || '';
  const photoList = parsePhotoList(raw);
  const covers = photoList.length > 0 ? photoList : cover ? [cover] : [];

  const emoji: ArticleEmoji = {
    text: '',
    pic: raw.emoji_photo || '',
  };

  /* ---------------- ✅ AR：同時支援 camelCase + snake_case ---------------- */

  // 1) 先吃 camelCase（你貼的後端）
  const arSpaceTypeFromCamel = normalizeArSpaceType(rawExt.arSpaceType);
  const arSpaceIdFromCamel = rawExt.arSpaceId ?? null;
  const arStoreIdFromCamel = rawExt.arStoreId ?? null;
  const arBranchNameFromCamel = rawExt.arBranchName ?? null;
  const arTitleFromCamel = rawExt.arTitle ?? null;

  // 2) 再 fallback 到 snake_case（舊 API）
  const arTypeFromSnake = normalizeArSpaceType((rawExt as any).ar_type ?? (raw as any).ar_type);
  const arSpaceIdFromSnake =
    (rawExt as any).ar_space_id ?? (raw as any).ar_space_id ?? (raw as any).branch_id ?? null;
  const arStoreIdFromSnake = (rawExt as any).ar_store_id ?? (raw as any).ar_store_id ?? null;
  const arBranchNameFromSnake =
    (rawExt as any).ar_branch_name ??
    (raw as any).ar_branch_name ??
    (raw as any).branch_name ??
    null;
  const arTitleFromSnake = (rawExt as any).ar_title ?? (raw as any).ar_title ?? null;

  const finalArSpaceType = arSpaceTypeFromCamel ?? arTypeFromSnake ?? null;
  const finalArSpaceId = arSpaceIdFromCamel ?? arSpaceIdFromSnake ?? null;
  const finalArStoreId = arStoreIdFromCamel ?? arStoreIdFromSnake ?? null;
  const finalArBranchName = arBranchNameFromCamel ?? arBranchNameFromSnake ?? null;
  const finalArTitle = arTitleFromCamel ?? arTitleFromSnake ?? null;

  const ar: ArticleAR = {
    isAR: toNumber(raw.is_ar) === 1,
    isActive: normalizeArIsActive((rawExt as any).ar_is_active ?? (raw as any).ar_is_active, true),

    // AR-內頁用
    arSpaceType: finalArSpaceType,
    arSpaceId: finalArSpaceId,
    arStoreId: finalArStoreId,
    arBranchName: finalArBranchName,
    arTitle: finalArTitle,

    // AR-列表用
    arType: finalArSpaceType,
    ar_store_id: finalArStoreId,
    ar_branch_name: finalArBranchName,
    ar_title: finalArTitle,
  };

  /* ---------------- 統計 ---------------- */

  const stats: ArticleStats = {
    views: toNumber(raw.look_num),
    shares: toNumber(raw.share_num),
    favorites: toNumber(raw.favorite_num),
    replies: toNumber(raw.message_num),
    praises: toNumber(raw.praise_num),
    receiveCount: toNumber(raw.receive_num),
    heat: toNumber(raw.heat_num),
  };

  /* ---------------- chip_give_data ---------------- */

  let chipGive: ArticleChipGiveMeta | undefined;

  const rawChip = rawExt.chip_give_data;
  if (rawChip) {
    let item: ChipGiveDataRaw | undefined;
    if (Array.isArray(rawChip)) item = rawChip[0];
    else if (typeof rawChip === 'object') item = rawChip as ChipGiveDataRaw;

    if (item) {
      chipGive = {
        id: item.id != null ? String(item.id) : '',
        type: item.type ?? '',
        typeId: item.type_id != null ? String(item.type_id) : '',
        typeName: item.type_name ?? '',
        typePhoto: item.type_photo ?? '',
        amount: toNumber(item.amount),
        quantity: toNumber(item.quantity),
        exAmount: toNumber(item.ex_amount),
        crtime: item.crtime ?? '',
        letime: item.letime ?? '',
        userId: item.user_id != null ? String(item.user_id) : '',
        isActive: toNumber(item.is_active) === 1,
      };
    }
  }

  /* ---------------- 內容 / 其他欄位 ---------------- */

  const content = raw.depiction ?? '';
  const shortData = parseShortData(
    rawExt.shortData ?? (raw as unknown as { shortData?: unknown }).shortData
  );

  const article: Article = {
    id,

    table,
    type: (raw.type as Article['type']) ?? 'text',
    bizType,
    articleTypeRaw,

    womoId,

    author,
    title: '',
    content,

    cover,
    covers,

    emoji,

    url: raw.url || undefined,
    tiktokId: raw.tiktok_id || undefined,
    music: raw.music || undefined,
    music_name: raw.music_name || undefined,
    shortData,

    ar,

    stats,

    coinStartDate: normalizeDate(rawExt.coin_start_date),
    coinEndDate: normalizeDate(rawExt.coin_end_date),
    chipGive,

    createdAt: normalizeDate(raw.create_time ?? raw.crtime),
    isEdit: !!raw.is_edit,

    storeId: raw.store_id,
    storeName: raw.store_name ?? null,
    storeOpenId: raw.store_open_id ?? null,
    storePhoto: (raw.store_photo_thumbnail || raw.store_photo) ?? null,

    intervalsTime: rawExt.intervals_time != null ? String(rawExt.intervals_time) : undefined,
    youtubeUrl: rawExt.youtube_url ?? undefined,
    isFav: toBoolFlag(rawExt.is_fav),
    isPraise: toBoolFlag(rawExt.is_praise),
    tagData: rawExt.tag_data ?? undefined,
  };

  return article;
}

/* -------------------------------------------------------
 * 留言：ArticleMessageItem -> ArticleMessage
 * ----------------------------------------------------- */

export function normalizeMessageList(items: ArticleMessageItem[]): ArticleMessage[] {
  return items.map((m) => {
    const baseUserId = m.user_id != null ? String(m.user_id) : '';
    const kolId = m.kol_id != null ? String(m.kol_id) : '';
    const womoId = m.womo_id != null ? String(m.womo_id) : '';
    const authorId = kolId || baseUserId;

    let authorName = (m.kol_name ?? '').trim();
    if (!authorName && typeof m.user_name === 'string') authorName = m.user_name.trim();
    if (!authorName && typeof m.womo_id === 'string') authorName = m.womo_id.trim();
    if (!authorName && baseUserId) authorName = baseUserId;

    const avatar =
      (m.kol_u_photo ?? '').trim() ||
      (m.kol_photo ?? '').trim() ||
      (m.user_u_photo ?? '').trim() ||
      (m.user_photo ?? '').trim() ||
      '';

    return {
      id: String(m.id ?? ''),
      parentId: String(m.parent_id ?? '0'),
      userId: baseUserId,
      originalUserId: m.original_user_id ? String(m.original_user_id) : undefined,
      content: m.content ?? '',
      childCount: toNumber(m.child_num),
      rewardNum: toNumber(m.reward_num),
      isSuper: !!m.is_super,
      isActive: m.is_active !== false,
      isEdit: !!m.is_edit,

      updateTime: normalizeDateTime(m.update_time),
      createTime: normalizeDateTime(m.create_time),

      womoId: womoId || undefined,
      authorId,
      authorName,
      authorAvatar: avatar || undefined,
    };
  });
}
